import { toCalendarIsoDay } from "./tradeDashboardDates";
import { formatStatDate } from "./dashboardStatsUtils";

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysBetween(from, to) {
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

function parseLocalDate(value) {
  const iso = toCalendarIsoDay(value);
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function isActiveAgent(agent) {
  return String(agent?.status || "Active").trim().toLowerCase() === "active";
}

function agentDisplayName(agent) {
  return [agent?.firstName, agent?.lastName].filter(Boolean).join(" ").trim() ||
    "Unnamed agent";
}

function nextBirthdayDate(dob) {
  const parsed = parseLocalDate(dob);
  if (!parsed) return null;
  const today = startOfToday();
  const month = parsed.getMonth();
  const day = parsed.getDate();
  let next = new Date(today.getFullYear(), month, day);
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = new Date(today.getFullYear() + 1, month, day);
    next.setHours(0, 0, 0, 0);
  }
  return next;
}

/** Birthdays from today through the next 7 days (one week before). */
export function getUpcomingBirthdays(agents, withinDays = 7) {
  const today = startOfToday();
  return (agents || [])
    .filter(isActiveAgent)
    .map((agent) => {
      const next = nextBirthdayDate(agent.dateOfBirth);
      if (!next) return null;
      const days = daysBetween(today, next);
      if (days < 0 || days > withinDays) return null;
      return {
        id: agent._id || agent.employeeNo,
        name: agentDisplayName(agent),
        employeeNo: agent.employeeNo,
        dateLabel: formatStatDate(next),
        days,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.days - b.days || a.name.localeCompare(b.name));
}

/** License expiries from today through the next 30 days (one month before). */
export function getUpcomingLicenseExpiries(agents, withinDays = 30) {
  const today = startOfToday();
  const rows = [];
  for (const agent of agents || []) {
    if (!isActiveAgent(agent)) continue;
    const licenses = Array.isArray(agent.licenses) ? agent.licenses : [];
    for (const license of licenses) {
      const expiry = parseLocalDate(license?.expiryDate);
      if (!expiry) continue;
      const days = daysBetween(today, expiry);
      if (days < 0 || days > withinDays) continue;
      rows.push({
        id: `${agent._id || agent.employeeNo}-${license.licenseNumber || license.licenseType || days}`,
        name: agentDisplayName(agent),
        employeeNo: agent.employeeNo,
        licenseType: license.licenseType || "License",
        dateLabel: formatStatDate(expiry),
        days,
      });
    }
  }
  return rows.sort((a, b) => a.days - b.days || a.name.localeCompare(b.name));
}

export function daysUntilLabel(days) {
  if (days === 0) return "today";
  if (days === 1) return "in 1 day";
  return `in ${days} days`;
}
